import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { CheckCircle } from 'lucide-react'
import { LuClock, LuSparkles, LuCheck, LuFlame } from 'react-icons/lu'
import { buildPlans, type PlanDuration, type Plan } from '#/data/plans'
import { usePlans } from '#/features/plans/hooks/usePlans'
import { cn } from '#/lib/utils'

const ACCENTS: Record<
  PlanDuration,
  {
    activeRing: string
    activeBorder: string
    activeBg: string
    badgeBg: string
    badgeText: string
    badgeBorder: string
    price: string
    iconBg: string
    iconColor: string
    tickBg: string
  }
> = {
  '30min': {
    activeRing: 'ring-neutral-800/20',
    activeBorder: 'border-neutral-800',
    activeBg: 'bg-neutral-50/80',
    badgeBg: 'bg-neutral-100',
    badgeText: 'text-neutral-700',
    badgeBorder: 'border-neutral-200',
    price: 'text-neutral-900',
    iconBg: 'bg-neutral-100',
    iconColor: 'text-neutral-700',
    tickBg: 'bg-neutral-800 text-white',
  },
  '45min': {
    activeRing: 'ring-primary-500/30',
    activeBorder: 'border-primary-500',
    activeBg: 'bg-primary-50/60',
    badgeBg: 'bg-amber-100/90',
    badgeText: 'text-amber-900',
    badgeBorder: 'border-amber-300',
    price: 'text-neutral-900',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-700',
    tickBg: 'bg-primary-500 text-neutral-950',
  },
  '60min': {
    activeRing: 'ring-secondary-600/30',
    activeBorder: 'border-secondary-600',
    activeBg: 'bg-secondary-50/60',
    badgeBg: 'bg-secondary-100',
    badgeText: 'text-secondary-800',
    badgeBorder: 'border-secondary-300',
    price: 'text-neutral-900',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-700',
    tickBg: 'bg-secondary-600 text-white',
  },
}

type PlanPickerProps = {
  name: string
  rules?: import('react-hook-form').RegisterOptions
}

export function PlanPicker({ name, rules }: PlanPickerProps) {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const { data: apiPricing } = usePlans()
  const plans = useMemo(() => buildPlans(apiPricing || {}), [apiPricing])

  const selected = watch(name) as string | undefined
  const error = errors[name]?.message as string | undefined

  // Normalize initial selected value if legacy key like 'standard-45min' was passed
  const normalizedSelected: PlanDuration = (() => {
    if (!selected) return '30min'
    if (selected === '30min' || selected === '45min' || selected === '60min')
      return selected as PlanDuration
    if (selected.includes('45min')) return '45min'
    if (selected.includes('60min') || selected.includes('1hour')) return '60min'
    return '30min'
  })()

  const handleSelect = (durationKey: PlanDuration) => {
    setValue(name, durationKey, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <div className="flex flex-col gap-3.5">
      {/* Label row */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-neutral-800">
          Choose a Plan Duration
          {rules?.required && <span className="ml-1 text-primary-500">*</span>}
        </p>
        {selected && (
          <span className="flex items-center gap-1.5 rounded-full bg-secondary-50 px-2.5 py-0.5 text-xs font-semibold text-secondary-700 border border-secondary-200">
            <CheckCircle className="h-3.5 w-3.5 text-secondary-600" />
            Plan selected
          </span>
        )}
      </div>

      {/* Plan options list */}
      <div
        className="flex flex-col gap-2.5"
        role="radiogroup"
        aria-label="Choose a Plan Duration"
      >
        {plans.map((plan: Plan) => {
          const accent = ACCENTS[plan.duration]
          const isSelected =
            selected === plan.duration ||
            (!selected && plan.duration === normalizedSelected)

          return (
            <button
              key={plan.duration}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleSelect(plan.duration)}
              className={cn(
                'group relative flex w-full items-center justify-between gap-3 rounded-2xl border-2 p-3 sm:p-3.5 text-left transition-all duration-200 cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                isSelected
                  ? cn(
                      'shadow-sm',
                      accent.activeBg,
                      accent.activeBorder,
                      `ring-2 ${accent.activeRing}`,
                    )
                  : 'border-neutral-200/90 bg-white hover:border-neutral-300 hover:bg-neutral-50/60 hover:shadow-xs',
              )}
            >
              {/* Left Column: Radio + Details */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Radio checkmark circle */}
                <div
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-150',
                    isSelected
                      ? cn('border-transparent', accent.tickBg)
                      : 'border-neutral-300 bg-white group-hover:border-neutral-400',
                  )}
                >
                  {isSelected && <LuCheck className="h-3 w-3 stroke-[3]" />}
                </div>

                {/* Duration Icon */}
                <div
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                    isSelected
                      ? accent.iconBg
                      : 'bg-neutral-100 text-neutral-600 group-hover:bg-neutral-200/70',
                  )}
                >
                  <LuClock
                    className={cn(
                      'h-4.5 w-4.5',
                      isSelected ? accent.iconColor : 'text-neutral-600',
                    )}
                  />
                </div>

                {/* Plan Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="text-sm font-bold text-neutral-900 leading-tight">
                      {plan.name}
                    </span>
                    {plan.badge && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap',
                          accent.badgeBg,
                          accent.badgeText,
                          accent.badgeBorder,
                        )}
                      >
                        {plan.duration === '45min' ? (
                          <LuSparkles className="h-2.5 w-2.5 shrink-0" />
                        ) : (
                          <LuFlame className="h-2.5 w-2.5 shrink-0" />
                        )}
                        {plan.badge}
                      </span>
                    )}
                  </div>

                  <p className="mt-0.5 text-xs text-neutral-500 leading-snug line-clamp-1">
                    {plan.subtitle}
                  </p>

                  <div className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-neutral-400">
                    <span>{plan.classMinutes} min/session</span>
                    <span>•</span>
                    <span>
                      {plan.daysPerWeek} days/wk ({plan.classesPerMonth}{' '}
                      classes/mo)
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column: Price */}
              <div className="shrink-0 text-right pl-2 sm:pl-3 border-l border-neutral-100/80">
                <div className="flex items-baseline justify-end gap-0.5 whitespace-nowrap">
                  <span
                    className={cn(
                      'text-lg sm:text-xl font-extrabold tracking-tight',
                      accent.price,
                    )}
                  >
                    ${plan.pricePerMonth}
                  </span>
                  <span className="text-xs font-medium text-neutral-400">
                    /mo
                  </span>
                </div>
                <span className="block text-[10px] font-semibold text-neutral-400 whitespace-nowrap mt-0.5">
                  $
                  {Math.round(plan.pricePerMonth / (plan.classesPerMonth || 8))}
                  /class
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Validation error */}
      {error && (
        <p role="alert" className="text-xs font-medium text-red-500">
          {error}
        </p>
      )}
    </div>
  )
}
